import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, X, CaretLeft } from '@phosphor-icons/react';
import { API, CATEGORY_LABELS, CONDITION_LABELS, DELIVERY_LABELS, photoUrl } from '../utils/marketplace';
import VoiceDictateButton from '../components/VoiceDictateButton';

const inputCls = 'w-full text-sm px-3 py-2.5 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:border-[#D95800]';
const labelCls = 'block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1';

export default function MarketplaceNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const token = localStorage.getItem('token');

  const [form, setForm] = useState({
    title: '', description: '', category: 'power-tools', condition: 'used',
    price: '', negotiable: false, quantity: 1, city: '', state: '',
    delivery_options: ['pickup'], photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const aiDraft = async () => {
    if (!form.photos.length) { toast.error('Add a photo first'); return; }
    setDrafting(true);
    try {
      const res = await axios.post(`${API}/api/marketplace/ai-draft`,
        { photo_file_ids: form.photos },
        { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data.draft;
      setForm((f) => ({
        ...f,
        title: d.title || f.title,
        description: d.description || f.description,
        category: d.category || f.category,
        condition: d.condition || f.condition,
        price: d.suggested_price ?? f.price,
      }));
      toast.success('Listing drafted from your photo — review and adjust!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI draft failed');
    } finally { setDrafting(false); }
  };

  useEffect(() => {
    if (!token) { navigate(`/login?redirect=/marketplace/new${editId ? `?edit=${editId}` : ''}`); return; }
    if (editId) {
      axios.get(`${API}/api/marketplace/listings/${editId}`)
        .then((res) => {
          const l = res.data.listing;
          setForm({
            title: l.title, description: l.description, category: l.category,
            condition: l.condition, price: l.price, negotiable: l.negotiable,
            quantity: l.quantity || 1, city: l.city, state: l.state,
            delivery_options: l.delivery_options || ['pickup'], photos: l.photos || [],
          });
        })
        .catch(() => toast.error('Could not load listing'));
    }
  }, [editId, token, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDelivery = (opt) => {
    setForm((f) => {
      const has = f.delivery_options.includes(opt);
      const next = has ? f.delivery_options.filter((d) => d !== opt) : [...f.delivery_options, opt];
      return { ...f, delivery_options: next.length ? next : f.delivery_options };
    });
  };

  const uploadPhotos = async (files) => {
    const remaining = 5 - form.photos.length;
    const toUpload = Array.from(files).slice(0, remaining);
    if (!toUpload.length) { toast.error('Maximum 5 photos'); return; }
    setUploading(true);
    try {
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/api/marketplace/upload-photo`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setForm((f) => ({ ...f, photos: [...f.photos, res.data.file_id] }));
      }
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.title.trim().length < 3) { toast.error('Title must be at least 3 characters'); return; }
    if (!form.description.trim() || form.description.trim().length < 10) { toast.error('Description must be at least 10 characters'); return; }
    if (form.price === '' || Number(form.price) < 0) { toast.error('Enter a price (0 for free)'); return; }
    if (!form.city.trim() || !form.state.trim()) { toast.error('City and state are required'); return; }
    setSaving(true);
    const payload = { ...form, price: Number(form.price), quantity: Number(form.quantity) || 1 };
    try {
      if (editId) {
        await axios.patch(`${API}/api/marketplace/listings/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Listing updated!');
        navigate(`/marketplace/listing/${editId}`);
      } else {
        const res = await axios.post(`${API}/api/marketplace/listings`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Listing published!');
        navigate(`/marketplace/listing/${res.data.listing._id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save listing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-16" data-testid="marketplace-new-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-bold text-neutral-500 hover:text-[#D95800]">
          <CaretLeft size={14} weight="bold" /> Back
        </button>
        <h1 className="text-2xl md:text-3xl font-black uppercase text-neutral-900 dark:text-white mt-3">
          {editId ? 'Edit Listing' : 'Sell an Item'}
        </h1>
        <p className="text-sm text-neutral-500 mt-1">List tools, leftover materials, or equipment. Buyers message you directly or buy instantly online — a 10% platform fee applies to online sales, credited straight to your wallet.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {/* Photos */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4">
            <p className={labelCls}>Photos (up to 5)</p>
            <div className="flex flex-wrap gap-2">
              {form.photos.map((p) => (
                <div key={p} className="relative w-20 h-20 border-2 border-black rounded-lg overflow-hidden">
                  <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => set('photos', form.photos.filter((x) => x !== p))}
                    className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full p-0.5"
                    data-testid={`remove-photo-${p}`}
                  >
                    <X size={12} weight="bold" />
                  </button>
                </div>
              ))}
              {form.photos.length < 5 && (
                <label className="w-20 h-20 border-2 border-dashed border-neutral-400 rounded-lg flex flex-col items-center justify-center cursor-pointer text-neutral-400 hover:border-[#D95800] hover:text-[#D95800] transition-colors" data-testid="listing-photo-upload">
                  <Camera size={22} weight="bold" />
                  <span className="text-[9px] font-black uppercase mt-0.5">{uploading ? '...' : 'Add'}</span>
                  <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={(e) => uploadPhotos(e.target.files)} />
                </label>
              )}
            </div>
            {form.photos.length > 0 && (
              <button
                type="button"
                onClick={aiDraft}
                disabled={drafting}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#0F172A] text-white font-black uppercase text-xs py-2.5 border-2 border-black shadow-[3px_3px_0px_#D95800] hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                data-testid="ai-draft-btn"
              >
                ✨ {drafting ? 'Reading your photo...' : 'Auto-fill listing from photo (AI)'}
              </button>
            )}
          </div>

          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 space-y-4">
            <div>
              <label className={labelCls}>Title</label>
              <input value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={120} placeholder="DeWalt 20V Max Drill — barely used" className={inputCls} data-testid="listing-title-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className={labelCls}>Description</label>
                <VoiceDictateButton
                  mode="note"
                  compact
                  onResult={({ transcript }) => {
                    if (!transcript) return;
                    set('description', `${form.description}${form.description.trim() ? '\n' : ''}${transcript}`.slice(0, 5000));
                  }}
                />
              </div>
              <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={5} maxLength={5000} placeholder="Condition details, brand, dimensions, why you're selling... or tap the mic and just talk" className={inputCls} data-testid="listing-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Category</label>
                <select value={form.category} onChange={(e) => set('category', e.target.value)} className={inputCls} data-testid="listing-category-select">
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Condition</label>
                <select value={form.condition} onChange={(e) => set('condition', e.target.value)} className={inputCls} data-testid="listing-condition-select">
                  {Object.entries(CONDITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Price ($)</label>
                <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="150" className={inputCls} data-testid="listing-price-input" />
              </div>
              <div>
                <label className={labelCls}>Quantity</label>
                <input type="number" min="1" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} className={inputCls} data-testid="listing-quantity-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-bold text-neutral-700 dark:text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={form.negotiable} onChange={(e) => set('negotiable', e.target.checked)} className="w-4 h-4 accent-[#D95800]" data-testid="listing-negotiable-checkbox" />
              Price negotiable (OBO)
            </label>
          </div>

          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>City</label>
                <input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Newark" className={inputCls} data-testid="listing-city-input" />
              </div>
              <div>
                <label className={labelCls}>State</label>
                <input value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="NJ" className={inputCls} data-testid="listing-state-input" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Delivery options</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(DELIVERY_LABELS).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleDelivery(k)}
                    className={`text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full transition-colors ${form.delivery_options.includes(k) ? 'bg-[#2B4C6F] text-white' : 'bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-400'}`}
                    data-testid={`listing-delivery-${k}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || uploading}
            className="w-full bg-[#D95800] text-white font-black uppercase text-sm py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform disabled:opacity-50"
            data-testid="listing-submit-btn"
          >
            {saving ? 'Saving...' : editId ? 'Save Changes' : 'Publish Listing'}
          </button>
        </form>
      </div>
    </div>
  );
}
